import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  APIService,
  CreateFormQuestionInput,
  CreateRozFormInput,
  FormQuestion,
  FormQuestionTemplate,
  RozForm,
  UpdateFormQuestionInput,
  UpdateRozFormInput,
} from '../../../app-sync.service';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RtcEvaluationsService } from '../rtc-evaluations.service';
import { FormsService } from '../../../shared/services/forms.service';
import { FeedbacksService } from '../../../shared/feedbacks/feedbacks.service';
import { SelectedOptions } from '../../../shared/interfaces/type-options';
import { interval, Observable, Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { appConstants } from '../../../shared/constants/constants';
import { ModalsService } from '../../../shared/modals/modals.service';
import { UsersService } from '../../users/users.service';
import { DocumentsService } from '../../documents/documents.service';
import { QuestionFromForms } from '../../../shared/interfaces/question-from-forms';
import { ValidatorsService } from '../../../shared/services/validators.service';
import { LocalBDService } from '../services/local-bd.service';

@Component({
  selector: 'app-rtc-evaluation',
  templateUrl: './rtc-evaluation.component.html',
  styleUrls: ['./rtc-evaluation.component.css'],
})
export class RtcEvaluationComponent implements OnInit, OnDestroy {
  @ViewChild('rtcSubmitModal', { static: false }) rtcSubmitModal:
    | TemplateRef<any>
    | undefined;
  @ViewChild('submissionSuccessContainer', { static: false })
  submissionSuccessContainer: any;
  @ViewChild('documentViewerModal', { static: false }) documentViewerModal:
    | TemplateRef<any>
    | undefined;
  @ViewChild('commentModal', { static: false }) commentModal:
    | TemplateRef<any>
    | undefined;
  @ViewChild('deleteImageModal', { static: false }) deleteImageModal:
    | TemplateRef<any>
    | undefined;
  isLoading: boolean = false;
  update: string = '';
  selectedOptions: SelectedOptions =
    this.rtcEvaluationsService.selectedOptions.value;
  questions: FormQuestionTemplate[] | FormQuestion[] = [];
  generalComment: string | null = null;
  evaluationForm: FormGroup;
  rozFormExists: boolean = false;
  isDev: boolean = environment.env === 'dev' || environment.env === 'sandbox';
  totalScore: number = 0;
  totalComments: number = 0;
  totalImages: number = 0;
  totalImagesToUpload: number = 0;
  evaluationResult: 'Aprobado' | 'Reprobado' = 'Reprobado';
  modalMessage: string = '';
  modalQuestion: string = '';
  modalTitle: string = '';
  showFormControls: boolean = false;
  isGettingURL: boolean = false;
  imageSource: string = '';
  commentForm: FormGroup;
  isSaving: boolean = false;
  // Estado para indicar que se está enviando la evaluación (botón Enviar evaluación)
  isSubmittingEvaluation: boolean = false;
  // Mensaje de éxito transitorio a mostrar luego del envío
  submitSuccessMessage: string | null = null;
  showSubmitSuccess: boolean = false;
  // Estado por pregunta para mostrar spinner cuando se está enviando la respuesta
  // puede ser 'Si' | 'No' | null (null significa no hay envío en curso)
  isSubmittingAnswer: ('Si' | 'No' | null)[] = [];
  // Estado por pregunta para sincronizaciones manuales (icono sync_problem)
  isSyncingQuestion: boolean[] = [];
  // Estado por pregunta para indicar que se está subiendo la imagen
  isUploadingImage: boolean[] = [];
  currentRozStatus: string = '';
  timeInterval: Observable<number> = interval(1000);
  // listener para eventos de red
  private offlineListener: any;
  // listener para evento 'online'
  private onlineListener: any;

  // evita reentradas en el proceso automático de sincronización al volver online
  private isAutoSyncingOnline: boolean = false;

  private optionsSelectedSubscription: Subscription = new Subscription();
  private timeIntervalSubscription: Subscription = new Subscription();

  get questionsControl() {
    return (<FormArray>this.evaluationForm.get('questions')).controls;
  }

  /**
   * Manejador para el click en Guardar dentro del modal de comentario.
   * Activa el spinner (isSaving) y cierra el modal cuando termina la operación.
   */
  onSaveClick(closeFn: any): void {
    if (!this.commentForm.valid || this.isSaving) return;
    this.isSaving = true;
    // Simulamos una operación de guardado (reemplazar con la lógica real si aplica)
    setTimeout(() => {
      this.isSaving = false;
      try {
        closeFn('Accept');
      } catch (e) {
        // en caso de que closeFn no sea una función válida
      }
    }, 700);
  }
  get commentControl() {
    return this.commentForm;
  }

  constructor(
    private formsService: FormsService,
    private feedbacksService: FeedbacksService,
    private api: APIService,
    private rtcEvaluationsService: RtcEvaluationsService,
    private modalsService: ModalsService,
    private usersService: UsersService,
    private validatorsService: ValidatorsService,
    private documentsService: DocumentsService,
    private LocalBDService: LocalBDService,
    private cdr: ChangeDetectorRef
  ) {
    this.evaluationForm = new FormGroup({
      comment: new FormControl(null),
      questions: this.formsService.formQuestionFormArrayInitialization(
        [],
        '',
        ''
      ),
    });
    this.commentForm = new FormGroup({
      comment: new FormControl(null),
    });
  }

  async ngOnInit(): Promise<void> {
    this.selectedOptions = this.rtcEvaluationsService.selectedOptions.value;
    // Si hay opciones seleccionadas cargamos el formulario
    if (
      this.rtcEvaluationsService.selectedOptionsDefined &&
      this.rtcEvaluationsService.selectedEntityExists
    ) {
      await this.refreshRozForm();
    }
    // Subscripción para actualizar formulario si cambian las opciones seleccionadas
    this.optionsSelectedSubscription =
      this.rtcEvaluationsService.selectedOptions.subscribe(
        async (selectedOptions: SelectedOptions): Promise<void> => {
          this.selectedOptions = selectedOptions;
          if (
            this.rtcEvaluationsService.selectedOptionsDefined &&
            this.rtcEvaluationsService.selectedEntityExists
          ) {
            // Si el vehículo existe cargamos la evaluación.
            await this.refreshRozForm();
          } else if (!this.rtcEvaluationsService.selectedEntityExists) {
            // Si el vehículo no existe, borramos la evaluación cargada previamente.
            this.showFormControls = false;
            this.questions = [];
            this.generalComment = null;
            // Ocultamos cualquier mensaje de envío previo si el usuario cambia a una patente inexistente
            this.showSubmitSuccess = false;
            this.submitSuccessMessage = null;
            this.initEvaluationForm();
          }
        }
      );
    // listener para detectar cuando el navegador queda offline y limpiar spinners pegados
    this.offlineListener = () => {
      try {
        this.clearAllSyncFlags('offline');
      } catch (e) {}
    };
    window.addEventListener('offline', this.offlineListener);
    // listener para detectar cuando el navegador vuelve online y reintentar sincronizaciones
    this.onlineListener = () => {
      try {
        this.onBrowserOnline();
      } catch (e) {}
    };
    window.addEventListener('online', this.onlineListener);
  }

  /**
   * Refresca el formulario cargado.
   * @return {Promise<void>}
   * @private
   */
  private async refreshRozForm(): Promise<void> {
    // El setTimeout es un workaround para evitar el error: https://v17.angular.io/errors/NG0100
    setTimeout((): void => {
      this.rtcEvaluationsService.disableLoadButton.next(true);
    }, 0);
    this.isLoading = true;
    await this.getRozForm().then((): void => {
      this.isLoading = false;
      // El setTimeout es un workaround para evitar el error: https://v17.angular.io/errors/NG0100
      setTimeout((): void => {
        this.rtcEvaluationsService.disableLoadButton.next(false);
      }, 0);

      const questionsArray = this.evaluationForm.get(
        'questions'
      ) as FormArray<FormGroup>;

      questionsArray.controls.forEach((group: FormGroup, index) => {
        Object.keys(group.controls).forEach((key) => {
          group.get(key)?.valueChanges.subscribe((value) => {
            if (['imageFile', 'isSync', 'isSyncImg'].includes(key)) return;
            console.log(`Cambio en index ${index}, campo ${key}:`, value);
            // Delegamos la responsabilidad a métodos más pequeños para reducir
            // la complejidad cognitiva del método padre.
            if (key === 'imageSourceFile' || key === 'imageLocalPath') {
              this.onQuestionImageChange(group, index);
            } else {
              this.onQuestionValueChange(group, index, key, value);
            }
          });
        });
      });
    });
  }

  /**
   * Contiene la lógica para cargar un formulario nuevo
   * o cargar un formulario preexistente.
   * @return {Promise<void>}
   * @private
   */
  private async getRozForm(): Promise<void> {
    // limpiamos cualquier mensaje de éxito previo
    try {
      this.showSubmitSuccess = false;
      this.submitSuccessMessage = null;
    } catch (e) {}

    const licensePlate: string = this.selectedOptions.licensePlate;
    // refrescar formulario y obtener datos
    await this.rtcEvaluationsService.refreshRozForm();
    const rozForm: RozForm = this.rtcEvaluationsService.getRozForm();
    const localData = await this.LocalBDService.getFormData(licensePlate);

    const { localDate, hasLocalData } = this.computeLocalDataState(localData);

    // decidir la acción a tomar según la combinación server/local
    if (this.shouldLoadNewForm(rozForm, hasLocalData)) {
      await this.loadNewForm();
      return;
    }

    if (this.isApproved(rozForm)) {
      this.handleApproved(licensePlate);
      return;
    }

    if (this.isProcessingOrSent(rozForm)) {
      this.handleProcessingOrSent();
      return;
    }

    if (this.shouldLoadFromLocal(rozForm, hasLocalData)) {
      // formulario existe solo en local
      this.loadFromLocal(localData, false);
      return;
    }

    // comparar fechas y decidir la fuente
    const serverDate = new Date(rozForm.updatedAt);
    if (serverDate < localDate) {
      this.loadFromLocal(localData, true);
    } else {
      this.loadFromServer(rozForm);
    }
  }

  private computeLocalDataState(localData: any): {
    localDate: Date;
    hasLocalData: boolean;
  } {
    let localDate = new Date(0);
    let hasLocalData = false;
    if (localData) {
      console.log('Local Data :', localData);
      localDate = new Date(localData.updatedAt);
      let currentMonthLocal = localDate.getMonth() + 1;
      const currentMonthFormatted: string =
        currentMonthLocal < 10
          ? '0' + currentMonthLocal
          : currentMonthLocal.toString();
      const isSameMounth =
        localDate.getFullYear().toString() + currentMonthFormatted ===
        this.rtcEvaluationsService.formDate;
      hasLocalData = localData.questions.length > 0 && isSameMounth;
    }
    return { localDate, hasLocalData };
  }

  private shouldLoadNewForm(rozForm: RozForm, hasLocalData: boolean): boolean {
    return Object.keys(rozForm).length === 0 && !hasLocalData;
  }

  private async loadNewForm(): Promise<void> {
    console.log('Cargar Formulario Nuevo');
    // Limpiamos cualquier mensaje de éxito previo al cargar una nueva evaluación
    this.showSubmitSuccess = false;
    this.submitSuccessMessage = null;
    this.rozFormExists = false;
    this.questions =
      await this.rtcEvaluationsService.getListFormQuestionTemplates();
    this.initEvaluationForm();
    this.showFormControls = true;
    //guardar formulario vacio
    this.createNewForm();
  }

  private isApproved(rozForm: RozForm): boolean {
    return rozForm.status === 'APPROVED';
  }

  private handleApproved(licensePlate: string): void {
    this.feedbacksService.showFeedback(
      `Evaluación del vehículo ${licensePlate} ya fue aprobada para este mes`,
      'info'
    );
    this.showFormControls = false;
  }

  private isProcessingOrSent(rozForm: RozForm): boolean {
    return ['PROCESSING', 'SENT'].includes(rozForm.status);
  }

  private handleProcessingOrSent(): void {
    // Mostrar la pantalla fija de éxito para el usuario (Evaluación enviada)
    // en lugar de un toast informativo para que el usuario vea el estado.
    this.submitSuccessMessage = 'Evaluación enviada correctamente.';
    this.showSubmitSuccess = true;
    this.showFormControls = false;
    // Intentamos dar foco al contenedor del mensaje para lectores de pantalla
    setTimeout(() => {
      try {
        const el =
          this.submissionSuccessContainer &&
          this.submissionSuccessContainer.nativeElement;
        if (el && typeof el.focus === 'function') el.focus();
      } catch (e) {
        // no bloquear en caso de error
      }
    }, 80);
  }

  private shouldLoadFromLocal(
    rozForm: RozForm,
    hasLocalData: boolean
  ): boolean {
    return Object.keys(rozForm).length === 0 && hasLocalData;
  }

  loadFromServer(rozForm: RozForm) {
    console.log('Cargar Formulario existente desde el servidor');
    this.rozFormExists = true;
    this.currentRozStatus =
      rozForm.status === 'SAVED' ? 'Guardado' : 'Reprobado';
    this.generalComment = rozForm.comment ? rozForm.comment : null;
    this.questions = <FormQuestion[]>rozForm.questions!.items;
    this.initEvaluationForm();
    this.showFormControls = true;
  }

  /**
   * Sincroniza manualmente una pregunta por índice (accion desde el icono sync_problem)
   * @param index índice de la pregunta en el formArray
   */
  onSyncQuestion(index: number): void {
    const questionsArray = this.evaluationForm.get(
      'questions'
    ) as FormArray<FormGroup>;
    const group = questionsArray.at(index) as FormGroup;
    if (!group) return;
    const question: QuestionFromForms = group.value;

    const updateRozFormQuestionInput: UpdateFormQuestionInput =
      this.buildUpdateRozFormQuestionInput(question);

    this.isSyncingQuestion[index] = true;
    // safety timeout: si la petición queda colgada, limpiamos los spinners para evitar que queden "pegados"
    const safetyTimeoutMs = 8000; // 8s
    let safetyTimer: any = setTimeout(() => {
      try {
        // aseguramos limpieza completa de flags y marcamos control como no sincronizado
        this.resetSyncFlags(index, group, question.order, 'timeout');
      } catch (e) {
        // ignore
      }
    }, safetyTimeoutMs);

    this.rtcEvaluationsService
      .updateRozFormQuestion(updateRozFormQuestionInput)
      .then(() => {
        // éxito de sincronización (mensaje de feedback eliminado para evitar toast verde)
        try {
          this.hideGlobalTooltip();
        } catch (e) {}
        // Marcamos el dato sincronizado
        group.patchValue({ isSync: true }, { emitEvent: false });
        group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.cdr.detectChanges();

        // Subimos imagen si aplica (método separado para reducir complejidad)
        return this.uploadImageIfPending(question, index, group);
      })
      .catch((err) => {
        // Si el error vino de upload, ya marcamos isSyncImg=false; igualmente mostramos feedback
        if (err && err.message === 'upload-failed') {
          this.feedbacksService.showFeedback(
            `Error al subir imagen de la pregunta Nro: ${question.order}`,
            'danger'
          );
        } else {
          this.feedbacksService.showFeedback(
            `Error al sincronizar pregunta Nro: ${question.order}`,
            'danger'
          );
          group.patchValue({ isSync: false }, { emitEvent: false });
          group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          this.cdr.detectChanges();
        }
        // En cualquier caso, limpiamos flags y marcamos control no sincronizado
        try {
          this.resetSyncFlags(index, group, question.order, 'error');
        } catch (e) {}
      })
      .finally(() => {
        // limpiamos timer de seguridad y flags
        try {
          clearTimeout(safetyTimer);
        } catch (e) {}
        this.isSyncingQuestion[index] = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Devuelve el texto adecuado para el tooltip del icono de sincronización
   * si la pregunta tiene una imagen pendiente sin sincronizar muestra el mensaje de error de imagen,
   * en otro caso devuelve el texto genérico para sincronizar la pregunta.
   */
  getSyncTooltipMessage(questionControl: FormGroup | any): string {
    try {
      const hasLocalImagePending = !!(
        questionControl?.get('imageSourceFile')?.value ||
        questionControl?.get('imageLocalPath')?.value
      );
      const isSyncImg = questionControl?.get('isSyncImg')?.value;
      if (hasLocalImagePending && isSyncImg === false) {
        return 'Error al cargar fotografía. Toca para reintentar';
      }
    } catch (e) {
      // ignore and fall through
    }
    return 'Sincronizar pregunta';
  }

  /** Tooltip solo para el icono de la pregunta (sin imagen) */
  getQuestionSyncTooltipMessage(questionControl: FormGroup | any): string {
    try {
      const isSync = questionControl?.get('isSync')?.value;
      if (isSync === false) return 'Sincronizar pregunta';
    } catch (e) {}
    return '';
  }

  /** Tooltip solo para el icono de imagen */
  getImageSyncTooltipMessage(questionControl: FormGroup | any): string {
    try {
      const hasLocalImagePending = !!(
        questionControl?.get('imageSourceFile')?.value ||
        questionControl?.get('imageLocalPath')?.value
      );
      const isSyncImg = questionControl?.get('isSyncImg')?.value;
      if (hasLocalImagePending && isSyncImg === false) {
        return 'Error al cargar fotografía. Toca para reintentar';
      }
    } catch (e) {}
    return '';
  }
  /**
   *
   * @param localData datos de formulario recuperados desde local
   * @param rozFormExists determina si el formulario ya esta creado en el servidor
   */
  loadFromLocal(localData: any, rozFormExists: boolean) {
    this.evaluationForm = new FormGroup({
      comment: new FormControl<string | null>(localData.comment),
      questions: this.formsService.formQuestionFormArrayInitialization(
        <FormQuestionTemplate[]>localData.questions,
        localData.plate,
        this.rtcEvaluationsService.formDate
      ),
    });
    this.currentRozStatus = 'Guardado';
    this.rtcEvaluationsService.refreshFromLocal(localData);
    this.rozFormExists = rozFormExists;
    this.showFormControls = true;

    const questions = this.evaluationForm.get('questions') as FormArray;

    // Inicialización y normalización de estado por pregunta
    this.initQuestionStateArrays(questions);
    this.normalizeImageSyncOnLoad(questions);

    // Reintentos automáticos si estamos online
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      this.retryPendingImagesIfOnline(questions);
      this.retryPendingAnswersIfOnline(questions);
    }

    // Calculamos la puntuación inicial
    this.updateScore();
  }

  private initQuestionStateArrays(questions: FormArray) {
    try {
      this.isSubmittingAnswer = questions.controls.map(() => null);
      this.isSyncingQuestion = questions.controls.map(() => false);
      this.isUploadingImage = questions.controls.map(() => false);
    } catch (e) {
      // no bloquear
    }
  }

  private normalizeImageSyncOnLoad(questions: FormArray) {
    try {
      questions.controls.forEach((group: AbstractControl, idx: number) => {
        try {
          const g = group as FormGroup;
          const s3 = g.get('imageS3Path')?.value;
          if (s3 && typeof s3 === 'string' && s3.trim() !== '') {
            g.patchValue({ isSyncImg: true }, { emitEvent: false });
            g.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            if (this.isUploadingImage && this.isUploadingImage[idx])
              this.isUploadingImage[idx] = false;
            if (this.isSyncingQuestion && this.isSyncingQuestion[idx])
              this.isSyncingQuestion[idx] = false;
          }
        } catch (e) {
          // continue
        }
      });
    } catch (e) {
      // no bloquear
    }
  }

  private retryPendingImagesIfOnline(questions: FormArray) {
    try {
      questions.controls.forEach((group: AbstractControl, idx: number) => {
        try {
          const g = group as FormGroup;
          const hasLocalImagePending = !!(
            g.get('imageSourceFile')?.value || g.get('imageLocalPath')?.value
          );
          const isSyncImg = g.get('isSyncImg')?.value;
          if (hasLocalImagePending && isSyncImg === false) {
            const question: QuestionFromForms = g.value;
            this.uploadImageIfPending(question, idx, g)
              .then(() => this.safeSaveOnLocal())
              .catch(() => this.safeSaveOnLocal());
          }
        } catch (e) {
          // continue
        }
      });
    } catch (e) {
      // ignore
    }
  }

  private retryPendingAnswersIfOnline(questions: FormArray) {
    try {
      questions.controls.forEach((group: AbstractControl, idx: number) => {
        try {
          const g = group as FormGroup;
          const isSync = g.get('isSync')?.value;
          const answer = g.get('answer')?.value;
          if ((answer === 'Si' || answer === 'No') && isSync === false) {
            const question: QuestionFromForms = g.value;
            this.processPendingAnswer(g, idx, question);
          }
        } catch (e) {
          // continue
        }
      });
    } catch (e) {
      // ignore
    }
  }

  private safeSaveOnLocal() {
    try {
      this.saveOnLocal();
    } catch (e) {}
  }

  /**
   * Inicializa el formulario para crear o
   * editar una evaluación.
   * @private
   */
  private initEvaluationForm(): void {
    // Inicializa el formulario con preguntas existentes o desde la plantilla.
    this.evaluationForm = new FormGroup({
      comment: new FormControl<string | null>(this.generalComment),
      questions: this.formsService.formQuestionFormArrayInitialization(
        <FormQuestionTemplate[]>this.questions,
        this.selectedOptions.licensePlate,
        this.rtcEvaluationsService.formDate
      ),
    });

    // Calculamos la puntuación inicial
    this.updateScore();
    // Inicializamos el array de estados de envío por pregunta
    const questions = this.evaluationForm.get('questions') as FormArray;
    this.isSubmittingAnswer = questions.controls.map(() => null);
    this.isSyncingQuestion = questions.controls.map(() => false);
    this.isUploadingImage = questions.controls.map(() => false);

    // Normalizar estado de sync de imagen al iniciar: si existe imageS3Path marcamos isSyncImg = true
    try {
      questions.controls.forEach((group: AbstractControl, idx: number) => {
        try {
          const g = group as FormGroup;
          const s3 = g.get('imageS3Path')?.value;
          if (s3 && typeof s3 === 'string' && s3.trim() !== '') {
            g.patchValue({ isSyncImg: true }, { emitEvent: false });
            g.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            // ensure related UI flags are cleared
            if (this.isUploadingImage && this.isUploadingImage[idx])
              this.isUploadingImage[idx] = false;
            if (this.isSyncingQuestion && this.isSyncingQuestion[idx])
              this.isSyncingQuestion[idx] = false;
          }
        } catch (e) {
          // continue
        }
      });
    } catch (e) {
      // no bloquear
    }
  }

  /**
   * Inicializa el formulario de comentario para preguntas.
   * @param {string | null} comment Comentario actual de la pregunta.
   * @private
   */
  private initCommentForm(comment: string | null): void {
    // Inicialización del formulario
    this.commentForm = new FormGroup({
      comment: new FormControl<string | null>(comment, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(300),
      ]),
    });
  }

  /**
   * Asigna el archivo al control imageSourceFile
   * de la pregunta.
   * @param {Event} event Evento input.
   * @param {number} index Índice de la pregunta en el arreglo questions.
   */
  onChangeImageFile(event: Event, index: number): void {
    // Asignamos el archivo al FormControl 'imageSourceFile'
    const target = event.target as HTMLInputElement;
    console.log(' --- onChangeImageFile ---');
    console.log(target);
    console.log(target.files![0]);
    if (target.files!.length > 0) {
      const file: File = target.files![0];
      // Asignamos el archivo
      (<FormArray>this.evaluationForm.get('questions'))
        .at(index)
        .patchValue({ imageSourceFile: file });

      // Creamos un lector de archivos local.
      const fileReader: FileReader = new FileReader();
      fileReader.onload = (): void => {
        (<FormArray>this.evaluationForm.get('questions'))
          .at(index)
          .patchValue({ imageLocalPath: fileReader.result });
      };
      fileReader.readAsDataURL(file);
      fileReader.onloadend = (): void => {
        this.saveOnLocal();
      };
    }
  }

  /**
   * Actualiza la puntuación y el estado que observa el usuario.
   */
  updateScore(): void {
    let criticalNumber: number = 0;
    let moderateNumber: number = 0;
    let mildNumber: number = 0;
    let scoreCheck: number = 0;
    this.totalScore = 0;

    this.evaluationForm.value.questions.forEach((question: any): void => {
      scoreCheck += question.weight;

      if (question.answer === 'No') {
        switch (question.criticality) {
          case 'LEVE':
            mildNumber += 1;
            break;
          case 'MODERADO':
            moderateNumber += 1;
            break;
          case 'CRITICO':
            criticalNumber += 1;
            break;
          default:
            break;
        }
      } else if (question.answer === 'Si') {
        this.totalScore += question.weight;
      }
    });

    // Criterios para Reprobar:
    //  - 1 respuesta No con nivel Crítico.
    //  - 3 respuestas No con nivel Moderado.
    //  - 5 respuestas No con nivel Leve.
    //  - Ponderación < 80%.
    if (
      criticalNumber >= 1 ||
      moderateNumber >= 3 ||
      mildNumber >= 5 ||
      this.totalScore < 80
    ) {
      this.evaluationResult = 'Reprobado';
    } else {
      this.evaluationResult = 'Aprobado';
    }
    this.saveOnLocal();
  }

  /**
   * Levanta el modal para aceptar la acción que quiere realizar el usuario
   * y ejecuta el método de creación/actualización del formulario
   * si se acepta el modal.
   * @param {string} action Acción que ejecutó el usuario.
   * @param {string} comment Comentario general de la evaluación.
   */
  async onSubmitEvaluation(
    action: 'save' | 'send',
    comment: string
  ): Promise<void> {
    if (!this.evaluationForm.valid && action === 'send') {
      this.feedbacksService.showFeedback(
        'Debe contestar todas las preguntas del formulario antes de poder enviarlo.',
        'danger'
      );
      return;
    }

    if (action === 'save') {
      await this.onAcceptSubmitEvaluation(action).then((): void => {
        this.isLoading = false;
      });
    } else {
      this.initCommentForm(comment);
      const licensePlate: string = this.selectedOptions.licensePlate;
      this.modalTitle = appConstants.rozForm.modalMessages.submitForm.title;
      this.modalQuestion =
        appConstants.rozForm.modalMessages.submitForm.question.replace(
          '_',
          licensePlate
        );
      this.modalMessage = appConstants.rozForm.modalMessages.submitForm.message;
      this.updateTotals();
      let modalResult: boolean = await this.modalsService.showModal(
        <TemplateRef<any>>this.rtcSubmitModal
      );
      if (modalResult) {
        // activamos spinner en el botón
        this.isSubmittingEvaluation = true;
        this.isLoading = true;
        this.evaluationForm.patchValue({
          comment: this.commentForm.get('comment')?.value,
        });
        await this.saveForm();
      }
    }
  }

  private updateTotals(): void {
    // Se calcula el total de comentarios e imágenes.
    this.totalComments = 0;
    this.totalImages = 0;
    this.totalImagesToUpload = 0;
    let totalImagesUploaded: number = 0;
    this.evaluationForm.value.questions.forEach((question: any): void => {
      if (question.comment !== null && question.comment !== '') {
        this.totalComments += 1;
      }
      if (question.imageS3Path !== null && question.imageS3Path !== '') {
        totalImagesUploaded += 1;
      }
      if (question.imageFile !== null && question.imageFile !== '') {
        this.totalImagesToUpload += 1;
      }
      this.totalImages = totalImagesUploaded + this.totalImagesToUpload;
    });
  }

  /**
   * Crea/Actualiza un formulario en la BD
   * @param {string} action Acción que ejecutó el usuario.
   */
  private async onAcceptSubmitEvaluation(
    action: 'save' | 'send'
  ): Promise<void> {
    const questionsArray = this.evaluationForm.get('questions') as FormArray;
    const unsyncedControls = questionsArray.controls.filter((control) => {
      const { isSync, isSyncImg } = control.value;
      return isSync === false || isSyncImg === false;
    });
    const uploadDateTimestamp: string = Math.floor(
      Date.now() / 1000
    ).toString();
    this.feedbacksService.showFeedback(`sincrinizando preguntas`, 'info');
    this.updateQuestions(unsyncedControls, uploadDateTimestamp);
  }

  async saveForm() {
    this.feedbacksService.showFeedback(`Guardando evaluación`, 'info');
    // 1. Actualizamos el formulario en la BD en estado Procesando
    let updateRozFormInput: UpdateRozFormInput = {
      formId: this.rtcEvaluationsService.formId,
      formDate: this.rtcEvaluationsService.formDate,
      status: 'PROCESSING',
      comment: this.evaluationForm.value.comment,
      updatedBy: this.usersService.getActiveUser().userId,
    };
    await this.api
      .UpdateRozForm(updateRozFormInput)
      .then(async () => {
        updateRozFormInput.status = 'SENT';
        await this.rtcEvaluationsService
          .updateRozForm(updateRozFormInput)
          .then(() => {
            //borrar Data local
            this.LocalBDService.deleteFormData(
              this.selectedOptions.licensePlate
            );
            this.feedbacksService.showFeedback(
              `Evaluación Enviada correctamente.`,
              'success'
            );
            // mostrar mensaje fijo personalizado (se cierra manualmente)
            this.submitSuccessMessage = 'Evaluación enviada correctamente.';
            this.showSubmitSuccess = true;
            // Intentamos dar foco al contenedor del mensaje para lectores de pantalla
            setTimeout(() => {
              try {
                const el =
                  this.submissionSuccessContainer &&
                  this.submissionSuccessContainer.nativeElement;
                if (el && typeof el.focus === 'function') el.focus();
              } catch (e) {
                // no bloquear en caso de error
              }
            }, 80);
          })
          .catch((error) => {
            console.log('error al Enviar');
          });
      })
      .catch((response: any): void => {
        this.feedbacksService.showErrorFeedbacks(
          response,
          `Error al actualizar evaluación del vehículo ${this.selectedOptions.licensePlate}`
        );
      });

    // Reiniciamos el formulario
    this.rtcEvaluationsService.resetRozForm();
    this.showFormControls = false;
    this.questions = [];
    this.generalComment = null;
    this.initEvaluationForm();
    this.timeIntervalSubscription.unsubscribe();
    this.isLoading = false;
    this.isSubmittingEvaluation = false;
  }

  /** Cierra el mensaje fijo de éxito mostrado tras enviar la evaluación */
  closeSubmitSuccess(): void {
    this.showSubmitSuccess = false;
  }

  /**
   * Acción llamada desde el botón "Ver detalles" en la vista de éxito.
   * Actualmente actúa como placeholder: muestra feedback y cierra la vista de éxito.
   * Se puede extender para navegar o abrir detalles del formulario si se requiere.
   */
  onOpenDetailsAfterSubmit(): void {
    this.feedbacksService.showFeedback(
      'Abriendo detalles de la evaluación',
      'info'
    );
    // Por ahora cerramos la vista de éxito para que el usuario regrese al flujo anterior.
    this.closeSubmitSuccess();
  }

  async createNewForm() {
    const questionList: QuestionFromForms[] =
      this.evaluationForm.value.questions.slice();
    const uploadDateTimestamp: string = Math.floor(
      Date.now() / 1000
    ).toString();
    const createRozFormInput: CreateRozFormInput = {
      formId: this.rtcEvaluationsService.formId,
      formDate: this.rtcEvaluationsService.formDate,
      status: 'SAVED',
      comment: this.evaluationForm.value.comment,
      createdBy: this.usersService.getActiveUser().userId,
      updatedBy: this.usersService.getActiveUser().userId,
    };

    await this.api
      .CreateRozForm(createRozFormInput)
      .then(async (): Promise<void> => {
        // 2. Creamos las preguntas.
        this.createQuestions(questionList, uploadDateTimestamp);
      })
      .catch((response: any): void => {
        this.feedbacksService.showErrorFeedbacks(
          response,
          `Error al crear evaluación del vehículo ${this.selectedOptions.licensePlate}`
        );
      });
  }

  /**
   * Crea una lista de preguntas en la BD.
   * @param {QuestionFromForms[]} questionList Lista de preguntas del formulario de evaluación.
   * @param {string} uploadDateTimestamp Timestamp de carga.
   */
  private createQuestions(
    questionList: QuestionFromForms[],
    uploadDateTimestamp: string
  ): void {
    for (let question of questionList) {
      // 2.1 Creamos la pregunta del formulario
      const createRozFormQuestionInput: CreateFormQuestionInput = {
        formQuestionId: this.updateFormQuestionId(
          question.formQuestionId,
          this.rtcEvaluationsService.formDate
        ),
        question: question.question,
        criticality: question.criticality,
        weight: question.weight,
        order: question.order,
        answer: question.answer,
        comment: question.comment,
        rozFormQuestionsFormId: this.rtcEvaluationsService.formId,
        rozFormQuestionsFormDate: this.rtcEvaluationsService.formDate,
      };
      this.rtcEvaluationsService
        .createRozFormQuestion(createRozFormQuestionInput)
        .then(() => {
          // 2.2 cargamos la imagen de la pregunta, si aplica.
          if (question.imageSourceFile) {
            this.uploadImage(question, uploadDateTimestamp);
          }
        });
    }
  }

  /**
   * Actualiza una lista de preguntas en la BD.
   * @param {QuestionFromForms[]} questionList Lista de preguntas del formulario de evaluación.
   * @param {string} uploadDateTimestamp Timestamp de carga.
   */
  private updateQuestions(
    questionControls: AbstractControl[],
    uploadDateTimestamp: string
  ): void {
    for (let control of questionControls) {
      const question = control.value;
      // 2.1 Actualizamos la pregunta del formulario
      const updateRozFormQuestionInput: UpdateFormQuestionInput = {
        formQuestionId: this.updateFormQuestionId(
          question.formQuestionId,
          this.rtcEvaluationsService.formDate
        ),
        question: question.question,
        criticality: question.criticality,
        weight: question.weight,
        order: question.order,
        answer: question.answer,
        comment: question.comment,
      };
      this.rtcEvaluationsService
        .updateRozFormQuestion(updateRozFormQuestionInput)
        .then(() => {
          control.patchValue({ isSync: true });
          this.feedbacksService.showFeedback(
            `pregunta Nro: ${question.order} sincronizada`,
            'success'
          );
          // 2.2 cargamos la imagen de la pregunta, si aplica.
          if (question.imageSourceFile) {
            this.uploadImage(question, uploadDateTimestamp)
              .then((resp) => {
                // feedback de imagen sincronizada eliminado para evitar toast verde
                control.patchValue({ isSyncImg: true });
              })
              .catch(() => {
                control.patchValue({ isSyncImg: false });
              });
          }
        })
        .catch(() => {
          control.patchValue({ isSync: false });
        });
    }
  }
  /**
   *
   * @param formQuestionId
   * @param newDate
   * @returns
   */
  updateFormQuestionId(formQuestionId: string, newDate: string): string {
    return formQuestionId.replace(/\d{6}(?=#\d{7}$)/, newDate);
  }

  /**
   * Carga la imagen de una pregunta
   * @param {QuestionFromForms} question Pregunta.
   * @param {string} uploadDateTimestamp timestamp de carga.
   */
  private uploadImage(
    question: QuestionFromForms,
    uploadDateTimestamp: string
  ): Promise<any> {
    const inputImage = {
      documentId: question.formQuestionId,
      documentMasterValueId: question.formQuestionId.split('#')!.pop()!,
      documentName: uploadDateTimestamp,
      documentSourceFile: question.imageSourceFile,
    };

    // Envolvemos la llamada en un timeout (safety) para que no quede la promesa colgada
    const timeoutMs = 8000; // 8 segundos
    return new Promise((resolve, reject) => {
      let finished = false;
      const timer = setTimeout(() => {
        if (!finished) {
          finished = true;
          reject(new Error('upload-timeout'));
        }
      }, timeoutMs);

      this.documentsService
        .uploadImage(
          inputImage,
          this.rtcEvaluationsService.formDate,
          uploadDateTimestamp,
          this.selectedOptions.business,
          this.selectedOptions.type,
          this.selectedOptions.licensePlate,
          question.order
        )
        .then((res) => {
          if (finished) return; // si ya se rechazó por timeout, ignoramos
          finished = true;
          clearTimeout(timer);
          resolve(res);
        })
        .catch((err) => {
          if (finished) return;
          finished = true;
          clearTimeout(timer);
          reject(err);
        });
    });
  }

  /**
   * Permite visualizar la imagen de una pregunta y borrarla.
   * @param {number} index Índice de la pregunta.
   */
  async onViewImage(index: number): Promise<void> {
    this.isGettingURL = true;

    const question: AbstractControl<any, any> = (<FormArray>(
      this.evaluationForm.get('questions')
    )).at(index);
    const imageS3Path: string | null = question.get('imageS3Path')?.value;
    const imageLocalPath: string | null = question.get('imageLocalPath')?.value;

    this.modalTitle = question.get('question')?.value;

    if (this.questionHasLocalImage(imageLocalPath)) {
      this.imageSource = <string>imageLocalPath;
    } else if (this.questionHasS3Image(imageS3Path)) {
      await this.documentsService
        .getImagePreSignedURL(<string>imageS3Path)
        .then(async (): Promise<void> => {
          this.imageSource = this.documentsService.documentPreSignedURL;
        });
    }

    await this.modalsService
      .showModal(<TemplateRef<any>>this.documentViewerModal, true)
      .then((value: boolean): void => {
        // Verificamos si el usuario presionó el botón de Borrar
        if (!value && this.modalsService.dismissedReason === 'with: Delete') {
          this.modalTitle =
            appConstants.rozForm.modalMessages.deleteImage.title;
          this.modalQuestion =
            appConstants.rozForm.modalMessages.deleteImage.question.replace(
              '_',
              question.get('question')?.value
            );
          this.modalMessage =
            appConstants.rozForm.modalMessages.deleteImage.message;

          this.modalsService
            .showModal(<TemplateRef<any>>this.deleteImageModal)
            .then((value: boolean): void => {
              if (value) {
                // Eliminamos todos los datos asociados a imágenes locales.
                (<FormArray>this.evaluationForm.get('questions'))
                  .at(index)
                  .patchValue({ imageFile: null });
                (<FormArray>this.evaluationForm.get('questions'))
                  .at(index)
                  .patchValue({ imageSourceFile: null });
                (<FormArray>this.evaluationForm.get('questions'))
                  .at(index)
                  .patchValue({ imageLocalPath: null });
                (<FormArray>this.evaluationForm.get('questions'))
                  .at(index)
                  .patchValue({ imageS3Path: null });

                // Si la pregunta tiene imagen en S3 actualizamos la pregunta en la BD
                if (this.questionHasS3Image(imageS3Path)) {
                  const updateFormQuestionInput: UpdateFormQuestionInput = {
                    formQuestionId: question.get('formQuestionId')?.value,
                    imageS3Path: null,
                  };
                  this.rtcEvaluationsService.deleteImageFromRozFormQuestion(
                    updateFormQuestionInput
                  );
                }
              }
            });
        }
      });
  }

  /**
   * Levanta el modal para ingresar un comentario de una pregunta.
   * @param {string | null} comment Comentario de la pregunta.
   * @param {string} question Enunciado de la pregunta.
   * @param {number} index Índice de la pregunta en el arreglo.
   */
  async onAddComment(
    comment: string | null,
    question: string,
    index: number
  ): Promise<void> {
    this.initCommentForm(comment);
    this.modalTitle = appConstants.rozForm.modalMessages.addComment.title;
    this.modalQuestion =
      appConstants.rozForm.modalMessages.addComment.question.replace(
        '_',
        question
      );
    this.modalMessage = appConstants.rozForm.modalMessages.addComment.message;

    let modalResult: boolean = await this.modalsService.showModal(
      <TemplateRef<any>>this.commentModal
    );

    if (modalResult) {
      (<FormArray>this.evaluationForm.get('questions'))
        .at(index)
        .patchValue({ comment: this.commentForm.get('comment')?.value });
      this.saveOnLocal();
    }
  }

  /**
   * Levanta el modal para ingresar un comentario general de la evaluación.
   * @param {string | null} comment Comentario de la pregunta.
   */
  async onAddGeneralComment(comment: string | null): Promise<void> {
    this.initCommentForm(comment);
    this.modalTitle = appConstants.rozForm.modalMessages.addComment.title;
    this.modalQuestion = 'Observación general de la evaluación.';
    this.modalMessage = appConstants.rozForm.modalMessages.addComment.message;

    let modalResult: boolean = await this.modalsService.showModal(
      <TemplateRef<any>>this.commentModal
    );

    if (modalResult) {
      this.evaluationForm.patchValue({
        comment: this.commentForm.get('comment')?.value,
      });
      this.saveOnLocal();
    }
  }

  /**
   * Actualiza la variable que monitorea si se stá descargando una imagen.
   */
  imageLoaded(): void {
    this.isGettingURL = false;
  }

  /**
   * Retorna un booleano que contesta a la pregunta
   * ¿la pregunta tiene imagen asociada en S3?
   * @param {string} imageS3Path Ruta de la imagen en S3.
   */
  questionHasS3Image(imageS3Path: string | null): boolean {
    return imageS3Path !== null && imageS3Path !== '';
  }

  /**
   * Retorna un booleano que contesta a la pregunta
   * ¿la pregunta tiene imagen asociada en local?
   * @param {string} imageLocalPath Ruta de la imagen en local.
   */
  questionHasLocalImage(imageLocalPath: string | null): boolean {
    return imageLocalPath !== null && imageLocalPath !== '';
  }

  /**
   * Retorna la clase para el ícono de comentario
   * @return {string}
   */
  getCommentClass(comment: string | null): string {
    if (comment !== null && comment !== '') {
      return 'with-content';
    } else {
      return 'without-content';
    }
  }

  /**
   * Determina, a través del servicio de validadores,
   * si debe mostrar la ayuda de un control del formulario.
   * @param {AbstractControl} control Control del formulario.
   * @return {Boolean}
   */
  showHelper(control: AbstractControl<any, any> | null): boolean | undefined {
    return this.validatorsService.showHelper(control);
  }

  /**
   * Consulta el mensaje de ayuda para un control del formulario.
   * @param {AbstractControl} control Control del formulario.
   * @return {string} Ayuda para el usuario.
   */
  helperMessages(control: AbstractControl<any, any> | null): string {
    return this.validatorsService.helperMessages(control);
  }

  /**
   * Muestra el formulario en la consola.
   * Nota: Solo aparece en DEV.
   */
  showForm(): void {
    console.log('-- Current Form --');
    console.log(this.evaluationForm);
  }

  /**
   * Retorna un color dependiendo del estado de la evaluación actual.
   * @return {string}
   */
  getColor(): string {
    return this.rtcEvaluationsService.getSatusColor();
  }

  /**
   * Retorna una imagen dependiendo del estado de la evaluación actual.
   * @return {string}
   */
  getImageSrc(): string {
    return this.rtcEvaluationsService.getRozFormImageSrc();
  }
  /**
   *Activa el Radio button cuando se toca el div.
   */
  selectRadio(radioId: string): void {
    const radio = document.getElementById(radioId) as HTMLInputElement;
    if (radio && !radio.checked) {
      radio.click();
    }
  }

  saveOnLocal() {
    console.log('Save On Local');
    this.LocalBDService.openDatabase().then(() => {
      this.LocalBDService.saveFormData(
        this.evaluationForm.value,
        this.selectedOptions.licensePlate
      ).then(() => {
        console.log('saved in indexedbd');
      });
    });
  }

  isNotSynced(control: AbstractControl): boolean {
    const isSync = control.get('isSync')?.value;
    const isSyncImg = control.get('isSyncImg')?.value;

    // Retorna true si alguno de los dos es falso
    return !isSync || !isSyncImg;
  }

  /**
   * Getter que retorna true solo si TODAS las preguntas
   * tienen sincronizados tanto el dato como la imagen.
   */
  get allQuestionsSynced(): boolean {
    const questions = this.evaluationForm.get('questions') as FormArray;
    return questions.value.every((question: QuestionFromForms) => {
      const isDataSynced = question.isSync === true;
      const isImageSynced =
        question.imageSourceFile || question.imageLocalPath
          ? question.isSyncImg === true
          : true;

      return isDataSynced && isImageSynced;
    });
  }

  ngOnDestroy(): void {
    this.optionsSelectedSubscription.unsubscribe();
    try {
      if (this.offlineListener)
        window.removeEventListener('offline', this.offlineListener);
      if (this.onlineListener)
        window.removeEventListener('online', this.onlineListener);
    } catch (e) {}
    console.log('rtc-evaluation.component subscriptions removed.');
  }

  /** Maneja la reconexión del navegador y reintenta sincronizar respuestas e imágenes pendientes */
  private onBrowserOnline(): void {
    // Evitar reentradas
    if (this.isAutoSyncingOnline) return;
    this.isAutoSyncingOnline = true;

    try {
      const questions = this.evaluationForm.get('questions') as FormArray;
      if (!questions) return;

      questions.controls.forEach((group: AbstractControl, idx: number) => {
        try {
          const g = group as FormGroup;
          const question: QuestionFromForms = g.value;

          const isSync = g.get('isSync')?.value;
          const answer = g.get('answer')?.value;
          if ((answer === 'Si' || answer === 'No') && isSync === false) {
            this.processPendingAnswer(g, idx, question);
          } else {
            this.processPendingImage(g, idx, question);
          }
        } catch (e) {
          // continue
        }
      });
    } catch (e) {
      // ignore
    } finally {
      // Liberamos la bandera luego de un pequeño delay para permitir otras reconexiones
      setTimeout(() => {
        this.isAutoSyncingOnline = false;
      }, 1000);
    }
  }

  private processPendingAnswer(
    group: FormGroup,
    idx: number,
    question: QuestionFromForms
  ): void {
    try {
      this.isSyncingQuestion[idx] = true;
      const safetyTimeoutMs = 8000;
      let safetyTimer: any = setTimeout(() => {
        try {
          this.resetSyncFlags(idx, group, question.order, 'timeout');
        } catch (e) {}
      }, safetyTimeoutMs);

      this.rtcEvaluationsService
        .updateRozFormQuestion(this.buildUpdateRozFormQuestionInput(question))
        .then(() => {
          try {
            this.hideGlobalTooltip();
          } catch (e) {}
          group.patchValue({ isSync: true }, { emitEvent: false });
          group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          this.cdr.detectChanges();
          return this.uploadImageIfPending(question, idx, group);
        })
        .catch(() => {
          try {
            group.patchValue({ isSync: false }, { emitEvent: false });
            group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.cdr.detectChanges();
          } catch (e) {}
          try {
            this.resetSyncFlags(idx, group, question.order, 'error');
          } catch (e) {}
        })
        .finally(() => {
          try {
            clearTimeout(safetyTimer);
          } catch (e) {}
          this.isSyncingQuestion[idx] = false;
          this.safeSaveOnLocal();
        });
    } catch (e) {
      // ignore
    }
  }

  private processPendingImage(
    group: FormGroup,
    idx: number,
    question: QuestionFromForms
  ): void {
    try {
      const hasLocalImagePending = !!(
        group.get('imageSourceFile')?.value ||
        group.get('imageLocalPath')?.value
      );
      const isSyncImg = group.get('isSyncImg')?.value;
      if (hasLocalImagePending && isSyncImg === false) {
        this.isUploadingImage[idx] = true;
        this.uploadImageIfPending(question, idx, group)
          .then(() => this.safeSaveOnLocal())
          .catch(() => this.safeSaveOnLocal())
          .finally(() => {
            this.isUploadingImage[idx] = false;
          });
      }
    } catch (e) {
      // ignore
    }
  }

  /** Limpia todos los flags de sincronización en la UI (usado por listener offline) */
  private clearAllSyncFlags(reason: string = 'manual'): void {
    try {
      const questions = this.evaluationForm.get('questions') as FormArray;
      if (!questions) return;
      for (let i = 0; i < questions.controls.length; i++) {
        try {
          // Limpiamos únicamente los flags transitorios de la UI para evitar
          // que se muestren iconos de sincronización en todas las preguntas
          // cuando el navegador queda offline. NO modificamos los flags
          // persistentes `isSync` / `isSyncImg` aquí.
          this.isSyncingQuestion[i] = false;
          this.isUploadingImage[i] = false;
          this.isSubmittingAnswer[i] = null;
        } catch (e) {}
      }
      // Ocultamos tooltip global si está visible
      try {
        this.hideGlobalTooltip();
      } catch (e) {}

      this.cdr.detectChanges();
      // Informamos al usuario que se limpió el estado temporal (no cambiamos
      // el estado de sincronización real de las preguntas).
      this.feedbacksService.showFeedback(
        `Limpieza de estado temporal por: ${reason}`,
        'info'
      );
      // Guardamos localmente sin modificar flags persistentes
      this.saveOnLocal();
    } catch (e) {}
  }

  /** Muestra el tooltip global fijado en pantalla para evitar clipping */
  showGlobalTooltip(event: MouseEvent, message: string): void {
    try {
      if (!message || (typeof message === 'string' && message.trim() === ''))
        return;
      const tooltip = document.getElementById('global-sync-tooltip');
      if (!tooltip) return;
      tooltip.innerText = message;
      const target = event.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      // Posicionamos encima del elemento, centrado horizontalmente
      const top = rect.top - 10; // 10px arriba
      const left = rect.left + rect.width / 2;
      tooltip.style.position = 'fixed';
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
      tooltip.style.transform = 'translate(-50%, -100%)';
      tooltip.setAttribute('aria-hidden', 'false');
      tooltip.style.opacity = '1';
      tooltip.style.visibility = 'visible';
      tooltip.style.zIndex = '999999';
    } catch (e) {
      // ignore
    }
  }

  hideGlobalTooltip(): void {
    const tooltip = document.getElementById('global-sync-tooltip');
    if (!tooltip) return;
    tooltip.style.opacity = '0';
    tooltip.style.visibility = 'hidden';
    tooltip.setAttribute('aria-hidden', 'true');
  }

  /** Limpia flags de sync/upload y marca el control como no sincronizado */
  private resetSyncFlags(
    index: number,
    group: FormGroup,
    questionOrder: number | string,
    reason: 'timeout' | 'error' | 'manual'
  ): void {
    try {
      console.log(`resetSyncFlags called for index=${index}, reason=${reason}`);
      if (this.isSyncingQuestion[index]) this.isSyncingQuestion[index] = false;
      if (this.isUploadingImage[index]) this.isUploadingImage[index] = false;
      // limpiar spinner de envío de respuesta si quedó marcado
      try {
        this.isSubmittingAnswer[index] = null;
      } catch (e) {}
      // marcamos control como no sincronizado para que el usuario vea el icono y pueda reintentar
      group.patchValue(
        { isSync: false, isSyncImg: false },
        { emitEvent: false }
      );
      group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      this.cdr.detectChanges();
      // ocultar tooltip global si está visible
      try {
        this.hideGlobalTooltip();
      } catch (e) {}
      const msg =
        reason === 'timeout'
          ? `Tiempo de espera agotado al sincronizar pregunta Nro: ${questionOrder}`
          : `Error al sincronizar pregunta Nro: ${questionOrder}`;
      this.feedbacksService.showFeedback(msg, 'danger');
      // guardamos en local para reflejar el estado
      this.saveOnLocal();
    } catch (e) {
      // no bloquear
    }
  }

  /** Establece la respuesta de la pregunta y oculta tooltips (usa valueChanges para continuar el flujo) */
  setAnswer(index: number, value: 'Si' | 'No'): void {
    try {
      const questions = this.evaluationForm.get('questions') as FormArray;
      const group = questions.at(index) as FormGroup;
      if (!group) return;
      group.get('answer')?.setValue(value);
      // actualizar score y ocultar tooltip si estaba abierto
      try {
        this.updateScore();
      } catch (e) {}
      try {
        this.hideGlobalTooltip();
      } catch (e) {}
    } catch (e) {
      // no bloquear
    }
  }

  /**
   * Total de preguntas en el formulario
   */
  get totalQuestions(): number {
    const questions = this.evaluationForm.get('questions') as FormArray;
    return questions ? questions.length : 0;
  }

  /**
   * Preguntas contestadas (answer === 'Si' || answer === 'No')
   */
  get answeredQuestions(): number {
    const questions = this.evaluationForm.get('questions') as FormArray;
    if (!questions) return 0;
    return questions.controls.filter((g) => {
      const ans = g.get('answer')?.value;
      return ans === 'Si' || ans === 'No';
    }).length;
  }

  /**
   * Porcentaje de progreso (0-100)
   */
  get progressPercent(): number {
    const total = this.totalQuestions;
    if (total === 0) return 0;
    return Math.round((this.answeredQuestions / total) * 100);
  }

  /** Maneja cambios relacionados a la imagen de la pregunta (subida) */
  private onQuestionImageChange(group: FormGroup, index: number): void {
    try {
      const question: QuestionFromForms = group.value;
      const uploadDateTimestamp: string = Math.floor(
        Date.now() / 1000
      ).toString();
      // marcamos estado de subida para mostrar spinner en la UI
      this.isUploadingImage[index] = true;
      this.cdr.detectChanges();
      this.uploadImage(question, uploadDateTimestamp)
        .then((resp) => {
          // historial: eliminamos feedback de éxito por cada imagen sincronizada
          group.get('isSyncImg')?.setValue(true, { emitEvent: false });
        })
        .catch(() => {
          group.get('isSyncImg')?.setValue(false, { emitEvent: false });
        })
        .finally(() => {
          this.isUploadingImage[index] = false;
          this.cdr.detectChanges();
        });
    } catch (e) {
      // no bloquear
    }
  }

  /** Maneja cambios en controles distintos a la imagen (respuesta, comentario, etc.) */
  private onQuestionValueChange(
    group: FormGroup,
    index: number,
    key: string,
    value: any
  ): void {
    try {
      const question: QuestionFromForms = group.value;
      const updateRozFormQuestionInput: UpdateFormQuestionInput = {
        formQuestionId: this.updateFormQuestionId(
          question.formQuestionId,
          this.rtcEvaluationsService.formDate
        ),
        answer: question.answer,
        comment: question.comment,
        criticality: question.criticality,
        imageS3Path: question.imageS3Path,
        order: question.order,
        question: question.question,
        weight: question.weight,
        [key]: value,
      };

      // Si el cambio fue en el control 'answer' y el valor es 'Si' o 'No', mostramos spinner solo para ese caso
      if (key === 'answer' && (value === 'Si' || value === 'No')) {
        this.isSubmittingAnswer[index] = value as 'Si' | 'No';
        this.rtcEvaluationsService
          .updateRozFormQuestion(updateRozFormQuestionInput)
          .then((response) => {
            group.get('isSync')?.setValue(true, { emitEvent: false });
          })
          .catch(() => {
            group.get('isSync')?.setValue(false, { emitEvent: false });
          })
          .finally(() => {
            this.isSubmittingAnswer[index] = null;
          });
      } else {
        // Para otros cambios no mostramos spinner pero sí intentamos sincronizar
        this.rtcEvaluationsService
          .updateRozFormQuestion(updateRozFormQuestionInput)
          .then((response) => {
            group.get('isSync')?.setValue(true, { emitEvent: false });
          })
          .catch(() => {
            group.get('isSync')?.setValue(false, { emitEvent: false });
          });
      }
    } catch (e) {
      // no bloquear
    }
  }

  /** Construye el objeto UpdateFormQuestionInput a partir de la pregunta */
  private buildUpdateRozFormQuestionInput(
    question: QuestionFromForms
  ): UpdateFormQuestionInput {
    return {
      formQuestionId: this.updateFormQuestionId(
        question.formQuestionId,
        this.rtcEvaluationsService.formDate
      ),
      answer: question.answer,
      comment: question.comment,
      criticality: question.criticality,
      imageS3Path: question.imageS3Path,
      order: question.order,
      question: question.question,
      weight: question.weight,
    };
  }

  /** Si existe imagen local pendiente la sube y actualiza el grupo; retorna Promise<void> */
  private uploadImageIfPending(
    question: QuestionFromForms,
    index: number,
    group: FormGroup
  ): Promise<void> {
    try {
      const hasLocalImagePending = !!(
        question.imageSourceFile || question.imageLocalPath
      );
      if (!hasLocalImagePending) {
        group.patchValue({ isSyncImg: true }, { emitEvent: false });
        group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.cdr.detectChanges();
        return Promise.resolve();
      }

      const uploadDateTimestamp: string = Math.floor(
        Date.now() / 1000
      ).toString();
      this.isUploadingImage[index] = true;
      this.cdr.detectChanges();
      return this.uploadImage(question, uploadDateTimestamp)
        .then(() => {
          group.patchValue({ isSyncImg: true }, { emitEvent: false });
          group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          this.cdr.detectChanges();
        })
        .catch(() => {
          group.patchValue({ isSyncImg: false }, { emitEvent: false });
          group.updateValueAndValidity({ onlySelf: true, emitEvent: false });
          this.cdr.detectChanges();
          throw new Error('upload-failed');
        })
        .finally(() => {
          this.isUploadingImage[index] = false;
          this.cdr.detectChanges();
        });
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
